"use client";

import type { ConstantsNotificationChannelResponse } from "@gosaas/types";
import * as React from "react";
import { toast } from "sonner";
import { useTeam } from "@/components/providers/team";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { apiClient, requestWithHandling } from "@/utils/api";

interface DeleteNotificationChannelDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	channel: ConstantsNotificationChannelResponse | null;
	onSuccess?: () => void;
}

export function DeleteNotificationChannelDialog({
	open,
	onOpenChange,
	channel,
	onSuccess,
}: DeleteNotificationChannelDialogProps) {
	const [isLoading, setIsLoading] = React.useState(false);
	const { teamID } = useTeam();

	async function onDelete() {
		if (!channel?.id) return;
		try {
			setIsLoading(true);
			const response = await requestWithHandling<void>(() =>
				apiClient.api.teamNotificationsChannelsDelete(
					teamID as string,
					channel.id as string,
				),
			);
			if (response.code !== "SUCCESS") {
				toast.error("Failed to delete channel", {
					description: response.message,
				});
				return;
			}
			toast.success("Channel deleted.");
			onOpenChange(false);
			onSuccess?.();
		} catch {
			toast.error("Couldn't delete this channel", {
				description: "Try again or reload the page.",
			});
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[440px]">
				<DialogHeader>
					<DialogTitle>Delete Channel</DialogTitle>
					<DialogDescription>
						Are you sure you want to delete <strong>{channel?.name}</strong>?
						This will also remove it from any rules that reference it.
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button variant="destructive" onClick={onDelete} disabled={isLoading}>
						{isLoading ? "Deleting..." : "Delete"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
